<template>
  <span class="m3-btn__anchor" :class="{ 'm3-btn__anchor--active': showDisabledTooltip }" @click="onAnchorTap">
    <component
      :is="rootTag"
      v-bind="{ ...rootAttrs, ...$attrs }"
      :class="rootClass"
      :disabled="isNativeDisabled || undefined"
      :aria-label="ariaLabel"
      :aria-busy="loading || undefined"
      :aria-disabled="(disable || loading) && !isNativeDisabled || undefined"
      :tabindex="tabIndex"
      :aria-describedby="describedById"
      @click="onClick"
      @focusin="onFocusin"
      @focusout="onFocusout"
      @keydown.esc="onEscape"
    >
      <span class="m3-icon-btn__state-layer" aria-hidden="true" />
      <q-icon
        v-if="!loading"
        :name="icon"
        :size="iconPixelSize"
        class="m3-icon-btn__icon"
      />
      <q-spinner-dots
        v-else
        :size="iconPixelSize"
        class="m3-icon-btn__spinner"
      />

      <!-- Styled tooltip — shares the .m3-btn__tooltip theme from BaseButton.
           Falls back to `ariaLabel` so icon-only buttons get a hover hint for
           free; pass an empty `tooltip=""` to suppress it (do that when the
           call site renders its own richer <q-tooltip> child).
           Stands down while the disabled bubble is up: `isNativeDisabled` is
           true only for a real <button>, so a disabled router-link/anchor still
           fires hover events and would show BOTH this and the wrapper's reason
           at once. The reason wins — it says more than the name does. -->
      <q-tooltip
        v-if="effectiveTooltip && !showDisabledTooltip"
        ref="enabledTooltipRef"
        class="m3-btn__tooltip"
        anchor="bottom middle"
        self="top middle"
        :offset="[0, 8]"
        :delay="400"
      >
        {{ effectiveTooltip }}
      </q-tooltip>

      <!-- Default slot — for nested Quasar overlay children like
           <q-menu>, <q-popup-proxy>. Renders inside the same DOM element so
           Quasar's anchor-based positioning works. -->
      <slot />
    </component>

    <!-- sr-only description: aria-describedby target, mirroring BaseButton. Only
         wired when the tooltip says something `ariaLabel` does not — the common
         auto-echo case would otherwise make screen readers announce the button
         twice. -->
    <span
      v-if="tooltipAddsInformation"
      :id="tooltipDescId"
      class="m3-btn__sr-desc"
    >{{ effectiveTooltip }}</span>

    <!-- The disabled reason, for screen readers. The q-tooltip below is a
         VISUAL affordance and announces nothing, so without this the whole
         explanation is hover-only - and call sites moved reasons out of
         `ariaLabel` into `disabledTooltip` precisely to stop the accessible
         name being a sentence. That trade is only sound if the reason still
         reaches assistive tech. -->
    <span
      v-if="showDisabledTooltip"
      :id="disabledDescId"
      class="m3-btn__sr-desc"
    >{{ disabledTooltip }}</span>

    <!-- Disabled-state tooltip — a natively disabled icon button suppresses its
         own hover events, so anchor the "why is this disabled?" hint to the
         layout-transparent wrapper span instead (see BaseButton for the
         pattern). The .m3-btn__anchor styles are defined in BaseButton. -->
    <q-tooltip
      v-if="showDisabledTooltip"
      ref="disabledTooltipRef"
      class="m3-btn__tooltip q-tooltip--tap-ok"
      anchor="bottom middle"
      self="top middle"
      :offset="[0, 8]"
      :delay="400"
    >
      {{ disabledTooltip }}
    </q-tooltip>
  </span>
</template>

<script setup lang="ts">
/**
 * IconButton — circular icon-only button (M3 spec).
 *
 * Four paint variants matching BaseButton intents:
 *   - filled    (high emphasis, brand-filled)
 *   - tonal     (medium emphasis, soft tinted)
 *   - outlined  (medium emphasis, border)
 *   - standard  (low emphasis, no chrome — the default toolbar icon)
 *
 * Always pass `ariaLabel` (this button has no visible label) — it doubles as
 * the hover/focus tooltip, so an icon-only button is never a mystery glyph.
 * Override the bubble text with `tooltip`, or suppress it with `tooltip=""`
 * (do that when the call site renders its own richer <q-tooltip> child).
 */

import { computed, onBeforeUnmount, ref, useId } from 'vue';
import { QIcon, QSpinnerDots, QTooltip } from 'quasar';
import type { RouteLocationRaw } from 'vue-router';

export type IconButtonSize = 'sm' | 'md' | 'lg';
export type IconButtonVariant = 'filled' | 'tonal' | 'outlined' | 'standard';

interface Props {
  /** Quasar icon name (required — it's an icon button). */
  icon: string;
  /** Accessible label, announced by screen readers. Required. */
  ariaLabel: string;
  /**
   * Styled hover / keyboard-focus / long-press tooltip. Defaults to
   * `ariaLabel` — an icon carries no text, so the bubble IS its label for
   * sighted mouse users. Pass a different string to say more than the
   * accessible name does, or `tooltip=""` to render none.
   */
  tooltip?: string;
  /**
   * Tooltip shown while the button is disabled or loading. A disabled button
   * suppresses its own hover events, so this anchors to a wrapper span instead
   * — use it to explain WHY the icon button is disabled.
   */
  disabledTooltip?: string;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  type?: 'button' | 'submit' | 'reset';
  to?: RouteLocationRaw;
  href?: string;
  loading?: boolean;
  disable?: boolean;
  external?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'standard',
  size: 'md',
  type: 'button',
  loading: false,
  disable: false,
  external: false,
});

const emit = defineEmits<{
  (e: 'click', event: MouseEvent): void;
}>();

// The bubble text: an explicit `tooltip` wins (empty string suppresses it),
// otherwise the accessible label is echoed. An icon-only button with no hover
// hint forces sighted users to guess the glyph or click to find out, so the
// echo is the default rather than something every call site opts into.
const effectiveTooltip = computed<string | undefined>(() => {
  if (props.tooltip !== undefined) return props.tooltip.trim() || undefined;
  return props.ariaLabel.trim() || undefined;
});

// Stable per-instance id linking the button to its sr-only description.
const tooltipDescId = useId();
const disabledDescId = useId();

// Only describe the button when the tooltip adds something beyond its name.
// In the common auto-echo case the two are identical, and a description equal
// to the accessible name makes screen readers announce the button twice.
const tooltipAddsInformation = computed(() => {
  const tip = effectiveTooltip.value;
  return tip !== undefined && tip !== props.ariaLabel.trim();
});

// While the button is dead, the reason IS the description. Pointing at the
// label bubble instead would announce a hint about an action the user cannot
// take, ahead of the reason they cannot take it.
const describedById = computed<string | undefined>(() => {
  if (showDisabledTooltip.value) return disabledDescId;
  return tooltipAddsInformation.value ? tooltipDescId : undefined;
});

// q-tooltip only opens on hover (and long-press on touch). Keyboard users
// reach the button by Tab and would otherwise never see the label, so drive
// the same bubble from focus — WCAG 2.2 SC 1.4.13 wants hover AND focus.
type TooltipInstance = { show: () => void; hide: () => void };
const enabledTooltipRef = ref<TooltipInstance | null>(null);
const disabledTooltipRef = ref<TooltipInstance | null>(null);

function onFocusin(): void {
  if (effectiveTooltip.value) enabledTooltipRef.value?.show();
}

function onFocusout(): void {
  if (effectiveTooltip.value) enabledTooltipRef.value?.hide();
}

// SC 1.4.13 "Dismissable": Escape closes the bubble without moving the
// pointer or focus. The event is left to bubble — a dialog that also closes
// on Escape keeps doing so, which is the expected layered behaviour.
function onEscape(): void {
  enabledTooltipRef.value?.hide();
  disabledTooltipRef.value?.hide();
}

// Touch has no hover, so the "why is this disabled?" reason would be
// unreachable there (hover hints are suppressed app-wide on coarse pointers —
// see @shared/tokens/css/_tooltips.scss; this bubble opts back in because its
// anchor is dead and tapping it can trigger nothing else). The disabled button
// carries pointer-events: none, so the tap lands on this wrapper. It closes
// itself since no mouseleave is coming.
const DISABLED_REASON_AUTO_HIDE_MS = 6000;
let disabledReasonTimer: ReturnType<typeof setTimeout> | null = null;

onBeforeUnmount(() => {
  if (disabledReasonTimer) clearTimeout(disabledReasonTimer);
});

function onAnchorTap(): void {
  if (!showDisabledTooltip.value) return;
  disabledTooltipRef.value?.show();
  if (disabledReasonTimer) clearTimeout(disabledReasonTimer);
  disabledReasonTimer = setTimeout(() => {
    disabledTooltipRef.value?.hide();
  }, DISABLED_REASON_AUTO_HIDE_MS);
}

// A disabled/loading icon button can't surface its own tooltip (no hover
// events), so anchor `disabledTooltip` to the wrapper span instead.
const showDisabledTooltip = computed(
  () => Boolean(props.disabledTooltip) && (props.disable || props.loading),
);

const rootTag = computed<'button' | 'a' | 'router-link'>(() => {
  if (props.to !== undefined) return 'router-link';
  if (props.href !== undefined) return 'a';
  return 'button';
});

const isNativeDisabled = computed(
  () => rootTag.value === 'button' && (props.disable || props.loading),
);

const tabIndex = computed<number | undefined>(() => {
  if (rootTag.value !== 'button' && (props.disable || props.loading)) return -1;
  return undefined;
});

const rootAttrs = computed<Record<string, unknown>>(() => {
  const attrs: Record<string, unknown> = {};
  if (rootTag.value === 'button') {
    attrs.type = props.type;
  } else if (rootTag.value === 'router-link') {
    attrs.to = props.to;
  } else if (rootTag.value === 'a') {
    attrs.href = props.href;
    if (props.external) {
      attrs.target = '_blank';
      attrs.rel = 'noopener noreferrer';
    }
  }
  return attrs;
});

const rootClass = computed(() => [
  'm3-icon-btn',
  `m3-icon-btn--${props.variant}`,
  `m3-icon-btn--size-${props.size}`,
  {
    'm3-icon-btn--loading': props.loading,
    'm3-icon-btn--disabled': props.disable && !props.loading,
  },
]);

const iconPixelSize = computed(() => {
  switch (props.size) {
    case 'sm': return '18px';
    case 'lg': return '26px';
    default:   return '22px';
  }
});

function onClick(event: MouseEvent): void {
  if (props.loading || props.disable) {
    event.preventDefault();
    event.stopPropagation();
    return;
  }
  // Quasar binds only mouseenter/mouseleave on the anchor, so a bubble opened
  // by hover survives the click that acts on it — and on the icon buttons that
  // host a <q-menu> the menu then opens directly underneath it. Once the user
  // has pressed, the label has done its job.
  enabledTooltipRef.value?.hide();
  emit('click', event);
}

// inheritAttrs: false — the root is now a layout-transparent wrapper span (for
// the disabled-tooltip anchor), so fall-through attrs (e.g. class="m3-icon-btn--on-dark",
// a call site's data-test) are forwarded onto the inner button via $attrs.
defineOptions({ name: 'IconButton', inheritAttrs: false });
</script>

<style lang="scss">
// Layout-transparent wrapper (also defined by BaseButton; duplicated here so
// IconButton is self-contained when used without BaseButton on a page). It
// generates no box until it must anchor the disabled-state tooltip.
.m3-btn__anchor {
  display: contents;

  &--active {
    display: inline-flex;
  }
}

// Same rationale as .m3-btn__anchor above: the aria-describedby target is
// plain text, so without this rule an IconButton rendered on a page that never
// pulls in BaseButton would paint its description as VISIBLE text next to the
// icon. Duplicated rather than imported to keep IconButton self-contained.
.m3-btn__sr-desc {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.m3-icon-btn {
  position: relative;
  isolation: isolate;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid transparent;
  border-radius: var(--btn-radius-pill);
  background: transparent;
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  overflow: hidden;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  transition:
    background-color var(--btn-transition),
    border-color var(--btn-transition),
    color var(--btn-transition),
    box-shadow var(--btn-transition),
    transform var(--btn-transition-press);

  &:focus { outline: none; }
  &:focus-visible {
    outline: var(--btn-focus-ring-width) solid var(--q-primary);
    outline-offset: var(--btn-focus-ring-offset);
  }

  &:active:not(.m3-icon-btn--disabled):not(.m3-icon-btn--loading) {
    transform: scale(var(--btn-press-scale));
  }

  // ── Size: square aspect, circular silhouette ────────────────────────────
  &--size-sm { width: 36px; height: 36px; }
  &--size-md { width: 44px; height: 44px; }
  &--size-lg { width: 56px; height: 56px; }

  // ── State / disabled ────────────────────────────────────────────────────
  &--disabled {
    opacity: 0.42;
    filter: saturate(0.6);
    cursor: not-allowed;
    pointer-events: none;
  }
  &--loading {
    cursor: progress;
    pointer-events: none;
  }

  &__state-layer {
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    background: currentColor;
    opacity: 0;
    transition: opacity var(--btn-transition);
  }
  &:hover:not(.m3-icon-btn--disabled):not(.m3-icon-btn--loading) .m3-icon-btn__state-layer { opacity: var(--btn-state-hover); }
  &:focus-visible:not(.m3-icon-btn--disabled):not(.m3-icon-btn--loading) .m3-icon-btn__state-layer { opacity: var(--btn-state-focus); }
  &:active:not(.m3-icon-btn--disabled):not(.m3-icon-btn--loading) .m3-icon-btn__state-layer { opacity: var(--btn-state-press); }

  &__icon, &__spinner {
    position: relative;
    z-index: 1;
    line-height: 0;
  }

  // ── Paint variants ──────────────────────────────────────────────────────
  &--standard {
    color: var(--text-color-secondary, var(--text-color));
  }

  &--filled {
    background: var(--btn-filled-bg);
    color: var(--btn-filled-fg);
    box-shadow: var(--btn-filled-shadow);

    &:hover:not(.m3-icon-btn--disabled):not(.m3-icon-btn--loading) {
      box-shadow: var(--btn-filled-shadow-hover);
    }
  }

  &--tonal {
    background: var(--btn-tonal-bg);
    color: var(--btn-tonal-fg);

    &:hover:not(.m3-icon-btn--disabled):not(.m3-icon-btn--loading) {
      background: var(--btn-tonal-bg-hover);
    }
  }

  &--outlined {
    color: var(--btn-outlined-fg);
    border-color: var(--btn-outlined-border);

    &:hover:not(.m3-icon-btn--disabled):not(.m3-icon-btn--loading) {
      border-color: var(--btn-outlined-border-hover);
      background: var(--btn-outlined-bg-hover);
    }
  }
}

// WhatsApp brand modifier — see BaseButton for the rationale. Stack on
// `filled` variant for a circular icon button using the messaging brand
// colour (e.g., the "send" button in a chat input).
.m3-icon-btn--whatsapp {
  background: var(--q-whatsapp, #118376) !important;
  // The dark-mode WhatsApp green is a LIGHT tone (#25D366): a white glyph on
  // it is 1.98:1, under even the 3:1 non-text floor. See --on-whatsapp.
  color: var(--on-whatsapp, #ffffff) !important;
  box-shadow: 0 6px 20px color-mix(in srgb, var(--q-whatsapp, #128C7E) 35%, transparent) !important;

  &:hover:not(.m3-icon-btn--disabled):not(.m3-icon-btn--loading) {
    background: color-mix(in srgb, var(--q-whatsapp, #128C7E) 88%, #000) !important;
  }

  &:focus-visible { outline-color: var(--q-whatsapp, #128C7E); }
}

// On-dark modifier — for IconButtons sitting in drawer headers, banners,
// hero overlays. Repaints the icon white and brightens the hover state
// layer. Stack on any variant — typically `standard` or `tonal`.
.m3-icon-btn--on-dark {
  color: #ffffff !important;

  &:hover:not(.m3-icon-btn--disabled):not(.m3-icon-btn--loading) {
    background: rgba(255, 255, 255, 0.10) !important;
  }

  &:focus-visible { outline-color: #ffffff; }
}

@media (prefers-reduced-motion: reduce) {
  .m3-icon-btn {
    transition: none;
    &:active:not(.m3-icon-btn--disabled):not(.m3-icon-btn--loading) { transform: none; }
    &__state-layer { transition: none; }
  }
}
</style>
